import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Wrench, GraduationCap, Calculator, ShoppingCart, Users, ExternalLink } from 'lucide-react';

const ResourceCard = ({ title, description, icon, links }) => (
  // Apply theme classes to Card
  <Card className="app-bg-secondary border-border flex flex-col">
    <CardHeader className="flex flex-row items-center space-x-4 pb-4">
      {/* Apply theme class to icon container */}
      <div className="text-accent-foreground">{icon}</div>
      <div>
        {/* Apply theme classes */}
        <CardTitle className="text-accent-foreground text-lg">{title}</CardTitle>
        {description && <CardDescription className="text-muted-foreground text-sm">{description}</CardDescription>}
      </div>
    </CardHeader>
    <CardContent className="flex-grow flex flex-col justify-between pt-2">
      <ul className="space-y-3 mb-4">
        {links.map((link, index) => (
          <li key={index} className="flex items-center justify-between text-sm">
            {/* Apply theme class */}
            <span className="text-muted-foreground">{link.name}</span>
            <Button
              variant="outline"
              size="sm"
              // Remove bg-accent, adjust hover
              className="h-7 px-2 text-accent-foreground border-border hover:bg-accent" 
              onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
              title={`Visit ${link.name} (opens in new tab)`}
            >
              Visit <ExternalLink size={12} className="ml-1" />
            </Button>
          </li>
        ))}
      </ul>
      {/* Optional: Add a note about affiliate links if desired */}
      {/* <p className="text-xs text-muted-foreground mt-auto text-center">Note: Some links may be affiliate links.</p> */}
    </CardContent>
  </Card>
);

const ResourcesPage = () => {
  const resources = [
    {
      title: 'Indemnity Insurance',
      description: 'Essential cover for dental professionals.',
      icon: <Shield size={24} />,
      links: [
        { name: 'Dental Protection', url: 'https://www.dentalprotection.org/uk' },
        { name: 'Densura', url: 'https://www.densura.com/' },
        { name: 'MDDUS', url: 'https://www.mddus.com/' },
        // { name: 'All Med Pro', url: 'https://www.allmedpro.co.uk/' }, // Example broker
      ],
    },
    {
      title: 'Equipment & Instruments',
      description: 'Suppliers for dental equipment and tools.',
      icon: <Wrench size={24} />,
      links: [
        { name: 'Henry Schein Dental', url: 'https://www.henryschein.co.uk/' },
        { name: 'Wright Dental / Trycare', url: 'https://www.trycare.co.uk/' }, // Combined as they merged
        { name: 'Kent Express', url: 'https://www.kentexpress.co.uk/' },
      ],
    },
    {
      title: 'CPD & Training',
      description: 'Continuing Professional Development resources.',
      icon: <GraduationCap size={24} />,
      links: [
        { name: 'ProDentalCPD', url: 'https://prodentalcpd.com/' },
        { name: 'Dental Update', url: 'https://www.dental-update.co.uk/' },
        { name: 'BDIA Events', url: 'https://www.bdia.org.uk/events' }, // Industry body events
      ],
    },
    {
      title: 'Accounting Software',
      description: 'Tools for managing self-employed finances.',
      icon: <Calculator size={24} />,
      links: [
        { name: 'FreeAgent', url: 'https://www.freeagent.com/' },
        { name: 'QuickBooks Self-Employed', url: 'https://quickbooks.intuit.com/uk/self-employed/' },
        { name: 'Xero', url: 'https://www.xero.com/uk/' },
        // { name: 'Coconut', url: 'https://getcoconut.com/' },
      ],
    },
    {
      title: 'Materials & Consumables',
      description: 'Suppliers for everyday clinical materials.',
      icon: <ShoppingCart size={24} />,
      links: [
        // Often same as equipment suppliers
        { name: 'Henry Schein Dental', url: 'https://www.henryschein.co.uk/' },
        { name: 'Wright Dental / Trycare', url: 'https://www.trycare.co.uk/' },
        { name: 'Kent Express', url: 'https://www.kentexpress.co.uk/' },
      ],
    },
    {
      title: 'Professional Bodies',
      description: 'Key organizations and regulatory bodies.',
      icon: <Users size={24} />,
      links: [
        { name: 'GDC (General Dental Council)', url: 'https://www.gdc-uk.org/' },
        { name: 'BSDHT', url: 'https://www.bsdht.org.uk/' },
        { name: 'BADN', url: 'https://badn.org.uk/' },
        // { name: 'BDA', url: 'https://www.bda.org/' }, // Primarily dentists
      ],
    },
  ];

  return (
    <div className="container mx-auto p-4 max-w-7xl space-y-6">
      {/* Apply theme class to title */}
      <h1 className="text-2xl sm:text-3xl font-bold text-accent-foreground mb-6">Resources for Dental Professionals</h1>

      {/* Apply theme class to paragraph */}
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Here are some useful links for indemnity insurance, equipment suppliers, CPD providers, and other resources relevant to self-employed dental hygienists, therapists, and nurses in the UK.
        <br />
        {/* Apply theme class */}
        <span className="text-sm text-muted-foreground/80">(Note: Coooly may earn a commission from affiliate links in the future. Currently, these are standard links.)</span>
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resources.map((resource) => (
          <ResourceCard
            key={resource.title}
            title={resource.title}
            description={resource.description}
            icon={resource.icon}
            links={resource.links}
          />
        ))}
      </div>
    </div>
  );
};

export default ResourcesPage;
